import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Wallet01Icon, 
  RefreshIcon,
  CheckmarkCircle01Icon
} from '@hugeicons/core-free-icons';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';
import { useWebSocket } from '../contexts/WebSocketContext';
import ResponsiveModal from './ResponsiveModal';
import Button from './Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import ReelText from './ui/ReelText';

const BalanceWidget = () => {
  const { user } = useAuth();
  const { echo, isConnected } = useWebSocket();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncedOnce, setSyncedOnce] = useState(false);
  const [accountData, setAccountData] = useState({
    balance: 0,
    currency: 'USD',
    deriv_id: null,
    last_fetched: null
  });

  const fetchBalance = async (isInitial = false, isManual = false) => {
    if (FLAGS.USE_MOCK_API) {
      setAccountData({ balance: 1250.00, currency: 'USD', deriv_id: 'VRTC8018118', last_fetched: new Date().toISOString() });
      return;
    }

    setLoading(true);
    try {
      if (isInitial) await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Use my-accounts for initial load as it's more robust
      const response = isManual 
        ? await api.post('/deriv/sync-balance') 
        : await api.get('/deriv/my-accounts');
      
      const rawData = response.data?.data || response.data;
      // Extract from profile structure or direct sync structure
      const account = rawData.deriv_account || rawData.account || rawData;
      
      if (account && account.deriv_id) {
        setAccountData({
          balance: account.current_balance,
          currency: account.balance_currency || 'USD',
          deriv_id: account.deriv_id,
          last_fetched: account.balance_last_updated_at || account.updated_at
        });
        setSyncedOnce(true);
      }
    } catch (err) { console.error('Failed to fetch balance:', err); } 
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.deriv_linked) fetchBalance(true);
  }, [user?.deriv_linked]);

  // WebSocket Listener + Polling Fallback
  useEffect(() => {
    if (!user || !user?.deriv_linked) return;

    let pollingInterval = null;

    if (echo && isConnected) {
      const channelName = `App.Models.User.${user.id}`;
      const channel = echo.private(channelName);
      channel.listen('.balance.updated', (data) => {
        setAccountData(prev => ({ ...prev, balance: data.balance, last_fetched: new Date().toISOString() }));
      });
    }

    // Persistent 5s poll as safety net
    pollingInterval = setInterval(() => {
      if (!document.hidden) fetchBalance(false);
    }, 5000);

    return () => {
      if (echo && user?.id) echo.leave(`App.Models.User.${user.id}`);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [echo, user?.id, user?.deriv_linked, isConnected]);

  const handleManualSync = (e) => {
    e.stopPropagation();
    fetchBalance(false, true);
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    if (!loading && user?.deriv_linked) {
      fetchBalance(false, true);
    }
  };

  if (!user?.deriv_linked) return null;

  const formattedBalance = parseFloat(accountData.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <button
        onDoubleClick={handleDoubleClick}
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 bg-muted/20 hover:bg-muted/40 rounded-full border border-border/50 transition-all group active:scale-95"
        title="Double-tap to sync"
      >
        <div className="p-1.5 rounded-full bg-soft-green/10 text-soft-green transition-colors">
          <HugeiconsIcon icon={Wallet01Icon} size={14} />
        </div>
        <div className="flex flex-col items-start leading-none pr-1">
          <p className="text-[8px] font-black uppercase text-muted-foreground tracking-widest mb-0.5">Balance</p>
          <div className="text-[11px] font-bold text-foreground flex items-center">
            <span className="mr-0.5">$</span>
            {!syncedOnce && loading ? '...' : (
              <ReelText text={formattedBalance} />
            )}
          </div>
        </div>
      </button>

      <ResponsiveModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Wallet Details"
      >
        <div className="py-2 space-y-6">
          <Card className="border-border/50 dark:bg-zinc-950/50 shadow-lg overflow-hidden">
            <CardHeader className="bg-muted/10 pb-6 border-b border-border/50 flex flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl overflow-hidden border border-border shadow-sm">
                  <img src="/deriv-logo.jpeg" alt="Deriv" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Deriv Linked</CardTitle>
                  <CardDescription className="text-lg font-mono font-bold text-foreground truncate max-w-[180px]">
                    {accountData.deriv_id || 'Loading...'}
                  </CardDescription>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleManualSync}
                disabled={loading}
                className={`rounded-full h-10 w-10 border-border/50 ${loading ? 'animate-spin' : 'hover:text-emerald-500'}`}
              >
                <HugeiconsIcon icon={RefreshIcon} size={18} />
              </Button>
            </CardHeader>
            <CardContent className="pt-8 pb-10 text-center space-y-2">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-muted-foreground">Available for Trade</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-5xl font-bold tracking-tighter text-foreground flex items-baseline">
                  <span className="text-2xl mr-1 text-muted-foreground/50">$</span>
                  <ReelText text={formattedBalance} />
                </span>
                <Badge variant="outline" className="mt-4 bg-emerald-500/10 text-emerald-500 border-none font-black h-5 uppercase">{accountData.currency}</Badge>
              </div>
            </CardContent>
          </Card>

          <div className="p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10 flex gap-3 items-start animate-in fade-in slide-in-from-bottom-2 duration-500">
            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} className="text-emerald-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Pro Tip: Fast Sync</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                You can <b>double-tap</b> the wallet widget in the top header at any time to instantly refresh your balance without opening this menu.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={() => setIsModalOpen(false)} className="w-full h-12 font-bold shadow-lg">Done</Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default BalanceWidget;